import type { GetWebhookDeliveriesQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetWebhookDeliveriesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import type { WebhookDeliveryRow } from './types';

export interface WebhookDeliveriesQueryArgs {
  webhookId: string;
}

// tRPC delivers timestamp columns as `Date` (SuperJSON), but the GraphQL
// `timestamptz` scalar is a string. Coerce so the mapped shape matches the
// generated query type regardless of which transport produced the row.
const toIso = (value: Date | string): string =>
  value instanceof Date ? value.toISOString() : value;

/**
 * Maps the tRPC delivery-log payload onto the `getWebhookDeliveries` GraphQL
 * shape. Nullable preview columns are coerced to `undefined` to match the
 * generated query type (Hasura returns `null`, the GraphQL codegen models
 * absent values as optional).
 */
export const mapWebhookDeliveriesToGraphQL = (
  data: WebhookDeliveryRow[] | undefined
): StrictMappedGraphQLSelection<GetWebhookDeliveriesQuery> => ({
  organisation_webhook_delivery: (data ?? []).map((delivery) => ({
    __typename: 'organisation_webhook_delivery' as const,
    Id: delivery.Id,
    WebhookId: delivery.WebhookId,
    EventId: delivery.EventId,
    EventType: delivery.EventType,
    Status: delivery.Status,
    DeliveryType: delivery.DeliveryType,
    HttpStatusCode: delivery.HttpStatusCode ?? undefined,
    ErrorMessage: delivery.ErrorMessage ?? undefined,
    ResponseBodyPreview: delivery.ResponseBodyPreview ?? undefined,
    DestinationUrl: delivery.DestinationUrl,
    DispatchedAt: toIso(delivery.DispatchedAt),
    CreatedAtTimestamp: toIso(delivery.CreatedAtTimestamp),
    Payload: delivery.Payload,
  })),
});

export const useListWebhookDeliveries = createQueryHook<
  WebhookDeliveriesQueryArgs,
  WebhookDeliveryRow[],
  GetWebhookDeliveriesQuery
>({
  trpcQueryOptions: (trpc, args) =>
    trpc.frontend.webhook.deliveryLog.list.queryOptions({
      webhookId: args.webhookId,
    }),
  mapTrpcDataToGraphQL: mapWebhookDeliveriesToGraphQL,
  graphqlDocument: GetWebhookDeliveriesDocument,
  graphqlVariables: (args) => ({ webhookId: args.webhookId }),
});
